
import { memo, ReactNode } from "react";
import { Handle, Position } from "@xyflow/react";
import { getShapeClassName } from "@/utils/shapeUtils";
import type { NodeShape } from "@/utils/shapeUtils";
import { cn } from "@/lib/utils";

interface NodeContainerProps {
  id: string;
  selected: boolean;
  nodeWidth: number;
  nodeHeight: number;
  shape?: NodeShape;
  isResizing: boolean;
  wasRecentlyEdited: boolean;
  editedBy?: string;
  isClientMode?: boolean;
  children: ReactNode;
  onMouseDown?: (event: React.MouseEvent) => void;
  isDragging?: boolean;
}

const NodeContainer = ({
  id,
  selected,
  nodeWidth,
  nodeHeight,
  shape = 'rectangle',
  isResizing,
  wasRecentlyEdited,
  editedBy,
  isClientMode = false,
  children,
  onMouseDown,
  isDragging = false
}: NodeContainerProps) => {
  const shapeClass = getShapeClassName(shape);
  
  
  
  return (
    <div
      data-id={id}
      className={cn(
        "flex flex-col text-sm font-medium relative",
        "",
        "reference-node-container",
        isResizing ? 'nodrag' : '',
        shapeClass,
        "node-shape-base",
        selected ? 'selected' : '',
        
        // Drag optimization classes
        "cursor-move select-none",
        isDragging ? 'dragging' : '',
        // Hardware acceleration
        "transform-gpu will-change-transform"
      )}
      style={{
        width: `${nodeWidth}px`,
        minWidth: "120px",
        maxWidth: `${nodeWidth}px`,
        height: `${nodeHeight}px`,
        minHeight: "50px",
        maxHeight: `${nodeHeight}px`,
        // Ensure proper hit area for dragging
        touchAction: 'none',
        // Improve drag performance
        contain: 'layout style paint',
      }}
      data-shape={shape}
      onMouseDown={onMouseDown}
    >
      <Handle 
        type="target" 
        position={Position.Top} 
        className="reference-handle reference-handle-top" 
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: '2px solid rgba(255, 255, 255, 0.95)',
          borderRadius: '50%',
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4), 0 0 0 1px rgba(102, 126, 234, 0.2)',
          width: '14px',
          height: '14px',
          zIndex: 20,
          top: '-7px',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      />
      
      {children}

      {wasRecentlyEdited && (
        <div className="absolute -top-8 right-0 reference-edit-badge animate-slide-in">
          <span className="text-xs font-semibold text-white px-2 py-1 rounded-full bg-gradient-to-r from-red-500 to-orange-500 shadow-lg">
            Edited by {editedBy}
          </span>
        </div>
      )}
      
      <Handle 
        type="source" 
        position={Position.Bottom} 
        className="reference-handle reference-handle-bottom" 
        style={{
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          border: '2px solid rgba(255, 255, 255, 0.95)',
          borderRadius: '50%',
          boxShadow: '0 4px 12px rgba(17, 153, 142, 0.4), 0 0 0 1px rgba(17, 153, 142, 0.2)',
          width: '14px',
          height: '14px',
          zIndex: 20,
          bottom: '-7px',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      />
      
    </div>
  );
};

export default memo(NodeContainer);
